import { createElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import GroundsViewChips from "../components/GroundsViewChips";
import { track } from "../lib/usageTracker";
import { foldVenuesFor, viewRows, withNearDistances } from "../lib/groundsView";

const DEEP_LINK_VIEWS = ["played", "near", "all"];

/** #1621 (D439): the `?view=` a deep link names, if it is one of the chips; anything else is the default view. */
export function viewFromSearch(search) {
  const v = new URLSearchParams(search || "").get("view");
  return DEEP_LINK_VIEWS.includes(v) ? v : "default";
}

/**
 * #1411 (D367.19) — the new-look Grounds page's own view state: which grounds the map shows (`default` own plus
 * nearby, `played`, `near`, `all`), who counts as "own", and the chips that change it. The shipped page never reads it.
 */
export function useGroundsView({ isCoach, groundsNearYou, savedVenues, redrawn, term, personalDone, nearMeReady, initialView = "default" }) {
  const [view, setView] = useState(redrawn ? initialView : "default");
  const added = groundsNearYou.addedAll;
  const foldVenues = useMemo(() => foldVenuesFor({ isCoach, added, savedVenues }), [isCoach, added, savedVenues]);
  const ownIds = useMemo(() => new Set(added.map((g) => g.ground_id)), [added]);
  const isOwn = useCallback((g) => (isCoach ? ownIds.has(g.ground_id) : g._played), [isCoach, ownIds]);
  const { nearMe, useMyLocation: locate } = groundsNearYou;
  // Pressing "Use my location" while the map shows played or all means "show me nearby too".
  // #1621: a deep link to `near` asks for the position once, as the Near me chip does (the new look only).
  const deepNear = useRef(redrawn && initialView === "near");
  useEffect(() => {
    if (!deepNear.current) return;
    deepNear.current = false;
    if (nearMe.status !== "ready") locate();
  }, [nearMe.status, locate]);
  const nearYou = useMemo(
    () => ({
      ...groundsNearYou,
      useMyLocation: () => {
        setView((v) => (v === "played" || v === "all" ? "default" : v));
        return locate();
      },
    }),
    [groundsNearYou, locate],
  );
  const chips = createElement(GroundsViewChips, {
    view,
    audience: isCoach ? "coach" : "player",
    positionKnown: nearMe.status === "ready",
    locating: nearMe.status === "locating",
    onPlayed: () => {
      track("grounds.chip", { item: "played" });
      setView("played");
    },
    onAll: () => {
      track("grounds.chip", { item: "all" });
      setView("all");
    },
    onNear: () => {
      track("grounds.chip", { item: "near_me" });
      setView("near");
      if (nearMe.status !== "ready") locate();
    },
  });
  const searching = Boolean(term);
  // The new look's rows: the view's pick of the filtered rows, with Near me's distances on whichever view it is.
  const applyView = useCallback(
    (chipped, nearById) => (redrawn ? withNearDistances(viewRows({ view, rows: chipped, isOwn, nearById, searching }), nearById) : null),
    [redrawn, view, isOwn, searching],
  );
  return {
    view,
    foldVenues,
    nearYou,
    chips,
    applyView,
    // Whether the position or the view cut the list down (what stops the server's total being quoted over it).
    narrowedByPlace: nearMeReady || (Boolean(redrawn) && view !== "all" && !(view === "default" && searching)),
    holdFirstFit: personalDone !== 3 || (Boolean(redrawn) && !groundsNearYou.loaded),
  };
}

export default useGroundsView;
